/**
 * The feed: the exploration as a conversation, and the column is its.
 *
 * Everything that happens renders here in the order it happened — each pass a
 * message whose reasoning STREAMS while the model works and stays where it was
 * read when it lands (a pass this tab did not watch reads its result first, the
 * trace one press under it — D341), each run ONE message that grows move by move, and the
 * reader's own questions and answers between them, their words in the chat's
 * own right-aligned bubble. The inspector no longer stands over any of it: it
 * is the node card at the head of this same scroll (see `Thread.tsx`), open
 * on a selection and one line when folded.
 *
 * Three sources, none of them new state. The record is `session.ops` and
 * `session.runs`, derived through `core/feed.ts` — the feed writes nothing, so
 * it can never disagree with the ledger, the run report or the node cards
 * about what happened. The live pass streams through the store's existing
 * `thought`/`searching` channels, which now carry the whole trace (the ticker
 * takes its own tail). And the conversation is `askLive`'s — one thread per
 * exploration under the key `'feed'`, stored beside the graph and never in it.
 *
 * The conversation ranges over many subjects where every earlier thread was
 * pinned to one, and the pin's promise — a conversation must never silently
 * become a conversation about something else — is kept per turn instead:
 * each question is stamped with what it was about when it was asked
 * (`StoredAbout`), the stamp is a press back to that node, and the answer's
 * history still travels with the whole conversation so a follow-up can lean on
 * what was said three subjects ago. The channel's other promises are unmoved:
 * no search, no gate, nothing said here lands on the graph, and the presses
 * under an answer run ordinary priced passes with the answer as INPUT
 * (invariant 9, D42).
 */

import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { auditFindingLabel } from '../core/auditReview'
import { useActiveEntry, useCanAsk, useStore, type ChatTarget, type RunLive } from '../core/store'
import {
  feedItems,
  liveProposalTurn,
  checkedAgainSince,
  passCheckResults,
  proposalState,
  recordedNarrations,
  turnAt,
  type FeedItem,
  type FeedTurn,
  type PassState,
} from '../core/feed'
import type { BriefChange } from '../core/brief'
import { shortDay } from '../core/dates'
import {
  BRIEF_CHANGE_WORD,
  EXECUTABLE_NOT_RUN,
  NO_TEST_ADDED,
  STATUS_WORD,
  pressEstimate,
} from '../core/vocabulary'
import { refusalsToPrint, upNextHere } from '../core/upNext'
import {
  DIRECTIVE_LABEL,
  LENS_LABEL,
  MOVE_REASON,
  OP_HINT,
  OP_LABEL,
  READING_LABEL,
  RUN_STOP_HEAD,
  kindChannelOf,
  modelPassesFor,
  moveResult,
  runProgress,
  type Op,
  type RunMove,
  type RunVerb,
} from '../core/types'
import { laterStatements, priorArtLabel, priorArtNote } from '../core/priorArt'
import { SWEEP_KIND_LABEL, sweepSentence } from '../core/sweep'
import { hostOf } from '../core/url'
import { PANEL_VERDICT_LABEL } from '../core/panel'
import {
  NARRATION_LABEL,
  executableOutcome,
  narrationWords,
  opLabel,
  type ExecutableRecord,
} from '../core/narrationReceipt'
import {
  EXECUTED_NOT_A_RESULT,
  executedSentence,
  rangesSentence,
} from '../core/executableConjecture'
import { admissionSentence } from '../core/conjecture'
import type { StoredAbout } from '../core/conversations'
import { orphanedVerificationEntries } from '../core/rebase'
import { endingLabelOf } from '../core/endings'
import { declinedMessage, passFailureNote, refusalSentence } from '../core/failure'
import { SINCE_REWRITTEN, rewrittenSince } from '../core/rewrite'
import {
  composerHasWords,
  liveKey,
  handsBack,
  sendOn,
  setLiveDraft,
  stopLive,
  stopNarration,
  streamingElsewhere,
  useLiveConversation,
  type AskEnd,
} from './askLive'
import { stopAsking } from '../engine/chat'
import { abortInFlight, auditorFor, cancelCurrent } from '../engine/reason'
import { approveMove, approveRest, declineHere, projectVerbUsd } from '../engine/run'
import { hasDemoReserve } from '../engine/demo'
import { ChannelNote, ExplainSimply } from './FeedNote'
import {
  ACT_NAME,
  actAuditor,
  actPressName,
  actPressWord,
  actPriceVerb,
  actReady,
  actRefusal,
  asActKind,
  gatePriceVerb,
  takeAct,
} from './actPress'
import { gateReady } from './runGate'
import { focusLivePass } from './cardFocus'
import {
  PROMOTE_PAID,
  PROMOTE_PRICE_VERB,
  promotableTurn,
  promotesFor,
  type PromoteKind,
} from '../core/promote'
import { promoteRefusal, takePromote } from './promotePress'
import { pointAtClaim, releaseClaim } from './proposalMark'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import { SpentMeta, VerbPrice } from './VerbPrice'
import { StanceStrip } from './StanceStrip'
import { scrollToCheckRecord } from './Grounding'
import { THOUGHT_TINT } from './Overlay'
import { PassClock, SearchTrail, UsualNote, useUsual } from './LiveWait'
import { UpNext } from './UpNext'
import { RunFound } from './RunFound'
import { ModePanel, ModeTabs, useNodeMode } from './NodeActs'
import { useReadFirst } from './SharedCopyBanner'
import { Icon } from './components/Icon'
import { Chevron } from './components/Chevron'
import { Prose } from './prose'

/** The narration's label in its two halves — its name, and what its words are
 *  not — as the card's head prints them (D352). One label, split, never two. */
const [VOICE_NAME, VOICE_UNCHECKED] = NARRATION_LABEL.split(' · ') as [string, string]

/** The feed's one conversation key. Not a target's — the subjects ride on the
 *  turns — so it can never collide with a pinned thread's `node:<id>`. */
const FEED_KEY = 'feed'

/** What a landed message reads for the search trail: one array, so its selector
 *  never re-renders the transcript's old passes while a live one searches. */
const NO_STEPS: string[] = []

/** When a Stop in this feed last ended a narration — the one under its words, or
 *  the phone box's — so the turn it stopped can take the focus that press lost. */
let voiceStoppedAt = 0

/**
 * The passes this tab watched land: their message was on screen while they
 * streamed AND when they finished. Their trace is words the reader was just
 * reading, so it stays open wherever the message is drawn again — after a turn
 * to another exploration and back — and only a reload forgets (D341). A pass
 * that landed while its message was not mounted (another exploration, a closed
 * sheet, a closed tab) was never watched to its end, and reads result first
 * with its trace folded, as history restored from disk does.
 *
 * Op ids only, never words: what it holds says how this tab saw a message, not
 * what the message said.
 */
const watchedLandings = new Set<string>()

/**
 * What the next question will be about, read at SEND time and stamped on the
 * turn. The selection is the subject — that is what "ask about this" has meant
 * on every surface since the composer moved into the pane — and with nothing
 * selected the question is about the whole graph.
 */
function currentTarget(): { target: ChatTarget; about: StoredAbout } {
  const s = useStore.getState()
  const node = s.selectedId ? s.session.nodes[s.selectedId] : null
  if (node) {
    return {
      target: { kind: 'node', id: node.id },
      about: { kind: 'node', id: node.id, label: node.label },
    }
  }
  const seed = s.session.seed.trim()
  return {
    target: { kind: 'graph' },
    about: {
      kind: 'graph',
      label: seed.length > 80 ? `${seed.slice(0, 79)}…` : seed || 'This exploration',
    },
  }
}

/** Ask one question on the feed's conversation. Exported for the composer's
 *  refusal check to sit beside — the send itself stays in one place. */
export function sendFeedQuestion(question: string): Promise<AskEnd | undefined> {
  const { target, about } = currentTarget()
  return sendOn(target, useStore.getState().session.id, FEED_KEY, question, about)
}

/**
 * A question that failed goes back to the box it left (`handsBack`), so it
 * survives the detour to the key field `askForKey` has just opened, or waits to
 * be pressed again after a rate limit: the composer on a desktop — unless
 * something has been typed there since — and the sheet's own box on a phone, or
 * the "Held for you" line when the composer is busy. Only on the exploration it
 * was asked about.
 */
function handBack(sessionId: string, question: string, narrow: boolean): void {
  const s = useStore.getState()
  if (s.session.id !== sessionId) return
  if (narrow || composerHasWords()) setLiveDraft(sessionId, FEED_KEY, question)
  else s.proposeSeed(question)
}

export function Feed({
  narrow,
  head,
  headKey = null,
}: {
  narrow: boolean
  /** What reads ABOVE the conversation in the same scroll — the thread's node
   *  card (`Thread.tsx`). One scroll, not two: the card used to sit in a
   *  nested region of its own over this one, and a 430px keyhole over 2,000px
   *  of card was the study's first finding. */
  head?: ReactNode
  /** Names the head while it is open — its anchor, plus a counter for a poke
   *  that asks to see it again — and null while it is folded. A change brings
   *  the head to the top of the scroll; see the effect below. */
  headKey?: string | null
}) {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const passBusy = useStore((s) => s.busy)
  const thought = useStore((s) => s.thought)
  const searching = useStore((s) => s.searching)
  const inRun = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const selectedId = useStore((s) => s.selectedId)
  const revealNode = useStore((s) => s.revealNode)
  const select = useStore((s) => s.select)
  const focusRun = useStore((s) => s.focusRun)
  // The chat role's key, which is what a question here is sent with.
  const ready = useCanAsk()

  const sessionId = session.id
  const k = liveKey(sessionId, FEED_KEY)
  const state = useLiveConversation(sessionId, FEED_KEY)
  const turns = state.turns
  const answering = turns.some((t) => t.live)
  const elsewhere = streamingElsewhere(k)

  const readFirst = useReadFirst()

  // The narrations this browser has no voice turn for — every one of them, on a
  // copy opened from a link or on the sender's own exploration opened on another
  // device — read off their receipts and set at their place in time. Joined
  // against `turns` so a narration this browser DID hear is never shown twice
  // (`recordedNarrations`). Only the feed's rows see them: the proposal and
  // promote rules below read the conversation itself.
  const told = useMemo(
    () => recordedNarrations(session, turns, narrationWords),
    [session, rev, turns],
  )
  const items = useMemo(
    () => feedItems(session, told.length ? [...turns, ...told] : turns, passBusy),
    [session, rev, turns, told, passBusy],
  )

  // The one turn whose proposals are still buttons — the newest that proposes
  // anything this surface would render. Every older proposal row folds to a line
  // saying what was proposed: a dozen priced Run buttons down a long thread were
  // a dozen offers written against graphs that no longer exist (`core/feed.ts`).
  const liveActs = useMemo(
    () =>
      liveProposalTurn(
        turns,
        (a) => asActKind(a.kind) !== null && session.nodes[a.node] !== undefined,
      ),
    [turns, session],
  )

  // Every press out of the feed reveals before it selects — the thread's own
  // pattern, because a subject or a produced node can sit inside a fold, and
  // selecting it bare would pivot the reading onto a card the map cannot show.
  // One function for the life of the feed, so the memoised rows below can tell
  // that nothing they were handed has changed (D335).
  const pivot = useCallback(
    (id: string) => {
      revealNode(id)
      select(id)
    },
    [revealNode, select],
  )

  const send = (q: string) => {
    const question = q.trim()
    if (!question || answering || elsewhere || !ready) return
    void sendFeedQuestion(question).then((kind) => {
      if (handsBack(kind)) handBack(sessionId, question, narrow)
    })
  }

  // A question handed over by the composer, for every subject the feed owns —
  // AskThread keeps the same latch for its pinned candidates, each consumer
  // guarded by the pin so one handoff can never be taken twice. Taken on the
  // TRANSITION, never the value: StrictMode invokes a mount effect twice
  // against one closure, and the value-read version asked twice and billed
  // twice (D37).
  const proposedQuestion = useStore((s) => s.proposedQuestion)
  const proposeQuestion = useStore((s) => s.proposeQuestion)
  const chatPin = useStore((s) => s.chat)
  const taken = useRef<string | null>(null)
  useEffect(() => {
    if (proposedQuestion === null) {
      taken.current = null
      return
    }
    if (chatPin?.kind === 'bridge') return
    if (taken.current === proposedQuestion) return
    taken.current = proposedQuestion
    proposeQuestion(null)
    if (ready && !elsewhere && !answering) send(proposedQuestion)
    // The composer pre-checks these and keeps its box on a refusal, so this
    // catch is for the race it cannot see — a stream that began between its
    // check and this effect. The words land in the feed's own draft, which the
    // phone's box renders and the desktop surfaces as a caught question below,
    // rather than falling between the two surfaces.
    else setLiveDraft(sessionId, FEED_KEY, proposedQuestion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedQuestion])

  /**
   * The feed follows its own tail the way a chat does — but only while the
   * reader is AT the tail. Scrolled up to reread a pass, nothing may move the
   * reading (the thread column's own rule, one pane over): new deltas and new
   * items land below without a scroll, and returning to the bottom re-arms the
   * follow.
   */
  const scroller = useRef<HTMLDivElement | null>(null)
  const stick = useRef(true)
  const lastTurn = turns.at(-1)
  // The Up next line sits at the tail too, and a press queued in a quiet stretch
  // of the pass (a search, say), or one coming back as a chip that asks again,
  // must not land below the fold. A string, so the selector compares by value.
  const upNextRows = useStore((s) => s.upNext.map((i) => (i.held ? `${i.id}?` : i.id)).join())
  // So is a line that says a request is waiting or has gone quiet — on the live
  // pass, or under the last turn — which arrives with no new words to follow.
  const passWaiting = useStore((s) => s.waiting)
  useEffect(() => {
    const el = scroller.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [
    items.length,
    thought,
    searching,
    lastTurn?.answer,
    lastTurn?.error,
    lastTurn?.waiting,
    passWaiting,
    upNextRows,
  ])

  /**
   * Selection drives the scroll, and only selection: a new anchor — or a poke
   * asking to see the card again — seats the card at the top of the one
   * scroll. DECLARED AFTER the tail-follow above, so on the render that mounts
   * both, this one runs last and the card wins over the tail. It also lets go
   * of the tail synchronously: waiting for the scroll event to say so left a
   * window in which a streaming delta pulled the reading straight back down.
   * The first placement is instant — easing toward a card the reader has not
   * seen yet animates nothing they can follow — and later walks ease, except
   * under reduced motion.
   */
  const placed = useRef(false)
  const headWas = useRef(headKey)
  useEffect(() => {
    const el = scroller.current
    const was = headWas.current
    headWas.current = headKey
    if (!el) return
    // Folded or deselected, the conversation owns the column again, and it
    // opens where a chat does — at its tail — rather than wherever the
    // vanished card left the scroll, which was the middle of some old pass.
    if (headKey === null) {
      if (was === null) return
      stick.current = true
      el.scrollTop = el.scrollHeight
      return
    }
    stick.current = false
    const smooth = placed.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    placed.current = true
    el.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' })
  }, [headKey])

  const lastItem = items.at(-1)

  /**
   * Something the reader started — a pass they pressed, anywhere, or a
   * question they sent — brings the conversation's tail into view and follows
   * it. The card sits above the conversation in the same scroll, so a verb
   * pressed on the card would otherwise stream its whole pass below the fold
   * while the reader watched nothing happen. A run's passes ride inside the
   * run's own message and never trip this, and neither does the voice: a
   * narration follows only a reader already at the tail. On the TRANSITION,
   * never the value: a pane that mounts while something is already streaming
   * (a phone's sheet raised by a node tap mid-pass) opens on the card it was
   * raised for, not on a pass it did not start.
   */
  const startedHere =
    lastItem?.kind === 'pass' && lastItem.state === 'live'
      ? lastItem.op.id
      : lastItem?.kind === 'turn' && lastItem.turn.live && !lastItem.turn.voice
        ? lastItem.turn.id
        : null
  const startedSeen = useRef(startedHere)
  useEffect(() => {
    const el = scroller.current
    if (startedHere === startedSeen.current) return
    startedSeen.current = startedHere
    if (startedHere === null) return
    if (!el) return
    stick.current = true
    el.scrollTop = el.scrollHeight
  }, [startedHere])

  // The composer's modes in the phone sheet's own box (D347), for the node the
  // sheet is open on.
  const phoneNode = narrow && selectedId ? (session.nodes[selectedId] ?? null) : null
  const phoneMode = useNodeMode(phoneNode)
  const phoneModeId = useId()
  // The promote row keeps D42's placement rule in the feed's terms: it renders
  // only when the LAST thing in the feed is a finished answer, about a node
  // still on the graph — anything that has landed since (a pass, a run ending)
  // sits between the reader and that answer, and a priced press must not reach
  // past what is directly above it. A VOICE turn never gets the row: the
  // narrator summarising a pass as "what the answer above argued" would feed a
  // paid pass its own echo.
  const promotable = promotableTurn(turns)
  const promoteNode =
    lastItem?.kind === 'turn' &&
    !lastItem.turn.voice &&
    promotable &&
    lastItem.turn.id === promotable.id &&
    promotable.about?.kind === 'node' &&
    promotable.about.id !== undefined
      ? (session.nodes[promotable.about.id] ?? null)
      : null

  const log = (
    <div
      className="feed scroll"
      ref={scroller}
      onScroll={(e) => {
        const el = e.currentTarget
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48
      }}
    >
      {head}

      {/* The channel's contract in one line, and the rest one press away
          (`ChannelNote`). The openers that sat under it went with the first
          turn; their one survivor is at the foot, above the box (`ExplainSimply`). */}
      <ChannelNote conversationKey={k} hasTurns={turns.length > 0} />

      {items.map((it, i) => {
        // The voice glues to the pass it narrates: a narration directly under
        // a pass or run message is the same speaker continuing, and chat
        // surfaces close the gap between consecutive messages from one
        // sender. Purely visual — the turn is its own record either way.
        // A change to the brief is not a speaker, so a narration under one is not
        // glued to it, and the seed is the reader speaking, so neither is one
        // under that.
        const prev = items[i - 1]
        const glue =
          it.kind === 'turn' &&
          it.turn.voice === true &&
          prev !== undefined &&
          prev.kind !== 'turn' &&
          prev.kind !== 'brief' &&
          prev.kind !== 'seed'
        // The stream goes to the one row that can be streaming — a live pass, or
        // the run whose pass is live — and every other row is handed the same
        // empty values on every delta. With `FeedRow` memoised, a thinking delta
        // then re-renders one message instead of the whole conversation, and a
        // long thread's old answers stop being re-read many times a second while
        // the reader types or presses Stop (D335).
        const streams =
          (it.kind === 'pass' && it.state === 'live') ||
          (it.kind === 'run' && it.passes.some((p) => p.state === 'live'))
        return (
          <FeedRow
            key={
              it.kind === 'pass'
                ? it.op.id
                : it.kind === 'turn'
                  ? it.turn.id
                  : it.kind === 'brief'
                    ? `brief-${it.change.what}-${it.at}`
                    : it.kind === 'seed'
                      ? 'seed'
                      : `${it.kind}-${it.run.id}`
            }
            item={it}
            // The pass right under the seed's bubble answers it, and is glued
            // to it the way a narration is to its pass (`PassMessage`).
            glue={glue || (it.kind === 'pass' && prev?.kind === 'seed')}
            actsLive={it.kind === 'turn' && it.turn.id === liveActs}
            thought={streams ? thought : ''}
            searching={streams ? searching : null}
            inRun={inRun}
            selectedId={selectedId}
            onPivot={pivot}
            onRun={focusRun}
          />
        )
      })}

      {/* What was pressed while the pass above was live, under it. */}
      <UpNext inFeed />

      {promoteNode && promotable && (
        <FeedPromotes
          node={promoteNode}
          question={promotable.question}
          answer={promotable.answer}
        />
      )}

      {/* Not on a copy being read first: it is an Ask, and the copy's composer
          is standing down until the reader takes it over. */}
      {!readFirst && (
        <ExplainSimply
          selected={selectedId ? (session.nodes[selectedId] ?? null) : null}
          ready={ready}
          narrow={narrow}
          phoneDraft={state.draft}
          answering={answering}
          elsewhere={elsewhere}
          onAsk={send}
        />
      )}

      {/* A question the handoff could not send — see the latch above. Rare,
          and the phone renders it in its own box below instead. */}
      {!narrow && state.draft.trim().length > 0 && (
        <p className="gnote gnote--quiet">
          Held for you: “{state.draft}”{' '}
          <button
            className="linkish"
            disabled={!ready || answering || elsewhere}
            onClick={() => {
              const q = state.draft
              setLiveDraft(sessionId, FEED_KEY, '')
              send(q)
            }}
          >
            Ask it
          </button>{' '}
          <button className="linkish" onClick={() => setLiveDraft(sessionId, FEED_KEY, '')}>
            Drop it
          </button>
        </p>
      )}

      {/* The phone's own box: the composer strip yields to this sheet while it
          is open, so without one the sheet could show answers and never take a
          question. The desktop deliberately has none — the composer at the
          pane's foot IS the input, which was the point of the re-homing.
          PINNED to the foot of the scroll (`.feed-ask` is sticky) rather than
          left at its end, for the reason the desktop composer is: with the card
          at the top of the same scroll, a box at the bottom of it was a box
          nobody could find. */}
      {narrow && !readFirst && (
        <div className="feed-ask" data-modes={phoneMode ? true : undefined}>
          {/* The composer's modes, in the sheet's own box (D347): the composer
              yields to this sheet on a phone, so the acts the card no longer
              carries ride here, Ask being this box. */}
          {phoneNode && phoneMode && (
            <ModeTabs node={phoneNode} mode={phoneMode} id={phoneModeId} />
          )}
          {phoneNode && phoneMode && phoneMode !== 'ask' ? (
            <ModePanel node={phoneNode} mode={phoneMode} id={phoneModeId} />
          ) : (
            <div
              className="feed-ask-row"
              id={phoneMode ? `${phoneModeId}-panel` : undefined}
              role={phoneMode ? 'tabpanel' : undefined}
              aria-labelledby={phoneMode ? `${phoneModeId}-ask` : undefined}
            >
              <textarea
                className="textarea"
                rows={1}
                value={state.draft}
                placeholder={
                  !ready
                    ? 'Connect a key to ask.'
                    : elsewhere
                      ? 'One thought at a time — another answer is still arriving.'
                      : selectedId
                        ? 'Ask about this finding…'
                        : 'Ask about this exploration…'
                }
                disabled={!ready || elsewhere}
                onChange={(e) => setLiveDraft(sessionId, FEED_KEY, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    const q = state.draft
                    setLiveDraft(sessionId, FEED_KEY, '')
                    send(q)
                  }
                }}
                aria-label="Ask about this exploration"
              />
              {/* Whatever is arriving here — an answer, or a narration, which
              `answering` counts and `stopAsking` never reached. */}
              {answering && (
                <button
                  className="btn btn--outline feed-ask-stop"
                  onClick={() => {
                    voiceStoppedAt = Date.now()
                    stopLive(k)
                  }}
                >
                  <Icon name="stop" size={13} />
                  Stop
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )

  return log
}

/** Memoised: see `streams` in `Feed`, which is what makes this pay. */
const FeedRow = memo(function FeedRow({
  item,
  glue,
  actsLive,
  thought,
  searching,
  inRun,
  selectedId,
  onPivot,
  onRun,
}: {
  item: FeedItem
  glue: boolean
  /** Whether this row's proposals are the live ones — see `liveProposalTurn`. */
  actsLive: boolean
  thought: string
  searching: string | null
  inRun: boolean
  selectedId: string | null
  onPivot: (id: string) => void
  onRun: (runId: string) => void
}) {
  if (item.kind === 'seed') return <p className="chat-you feed-seed">{item.text}</p>
  if (item.kind === 'brief') return <BriefMessage change={item.change} />
  if (item.kind === 'run')
    return (
      <RunMessage
        item={item}
        thought={thought}
        searching={searching}
        inRun={inRun}
        onPivot={onPivot}
        onRun={onRun}
      />
    )
  if (item.kind === 'turn')
    return (
      <TurnMessage
        turn={item.turn}
        glue={glue}
        actsLive={actsLive}
        selectedId={selectedId}
        onPivot={onPivot}
      />
    )
  return (
    <PassMessage
      op={item.op}
      state={item.state}
      move={item.move}
      underSeed={glue}
      thought={thought}
      searching={searching}
      inRun={inRun}
      onPivot={onPivot}
    />
  )
})

/**
 * A change to the standing brief, where it fell between passes (`briefChanges`):
 * a directive added on the third day, one reworded, one removed. One quiet line,
 * not a message — nothing was said, but every pass below it was told something
 * the passes above it were not. Derived from the dates the brief keeps, so it
 * presses nothing and calls nothing; the list itself is under Adjust.
 *
 * An edit says the words it replaced after the words in force, in words rather
 * than a strike-through a screen reader does not announce.
 */
function BriefMessage({ change }: { change: BriefChange }) {
  return (
    <p className="feed-brief">
      <i className="feed-dot" aria-hidden />
      <span className="feed-brief-body">
        <span className="feed-brief-word">{BRIEF_CHANGE_WORD[change.what]}</span>
        {change.lines.map((l, i) => (
          <span key={i} className="feed-brief-line">
            {i === 0 ? ' — ' : ' · '}
            {DIRECTIVE_LABEL[l.kind]}: “{l.text}”
            {l.was !== undefined && <span className="feed-brief-was"> (was “{l.was}”)</span>}
          </span>
        ))}
      </span>
      <span className="feed-pass-meta">{shortDay(change.at)}</span>
    </p>
  )
}

/**
 * A whole run as ONE growing message — the chat's answer to a working partner.
 *
 * While the run is live, the header carries the current move and the pass in
 * flight streams its reasoning INSIDE this message, exactly as a lone pass
 * does; each move that lands becomes a compact row rather than a message of
 * its own, so a 24-move run stays one bubble between the reader and their last
 * question. The rows fold behind the header once the run ends, and what it came
 * to reads beneath them in four lines (`RunFound`); the full
 * accounting stays one press away in the run report, which remains the record
 * (`moveResult` is the same sentence that report prints, so the two surfaces
 * cannot describe one move two ways).
 */
function RunMessage({
  item,
  thought,
  searching,
  inRun,
  onPivot,
  onRun,
}: {
  item: Extract<FeedItem, { kind: 'run' }>
  thought: string
  searching: string | null
  inRun: boolean
  onPivot: (id: string) => void
  onRun: (runId: string) => void
}) {
  const { run, passes } = item
  const [open, setOpen] = useState(false)
  // What `opLabel` reads to name an executable test; a run never proposes one, but
  // a row's name is read by the one rule wherever an op is named.
  const research = useStore((s) => s.session.research)
  const livePass = passes.find((p) => p.state === 'live')
  // A run with no ending is live only while the store says a run is going —
  // otherwise it is one the tab closed under, `Op.finishedAt`'s own rule one
  // level up.
  const live = run.finishedAt === undefined && inRun
  const died = run.finishedAt === undefined && !inRun
  const expanded = live || open
  // The live projection, and only when it is THIS run's: the feed keeps every
  // finished run's message, and a gate rendered off another run's pause would
  // put a go button under the wrong record.
  const pod = useStore((s) => s.run)
  const gate = live && pod !== null && pod.id === run.id ? pod : null

  // Logical moves off the record, passes named apart — `runProgress`, the strip's
  // and the account's own sentence. This used to count the passes riding this
  // item and call them moves, so a portfolio run read "8 of 12 moves" here while
  // its account said all twelve were spent.
  const meta = [
    runProgress(run.moves.length, run.budget.moves, modelPassesFor(run.moves)),
    run.produced > 0 ? `${run.produced} node${run.produced === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="feed-run" data-live={live || undefined}>
      <div className="feed-pass-head">
        {/* The pass's byline, said once for the whole run (D352). */}
        <i className="feed-mark" data-live={live || undefined} aria-hidden />
        <b className="feed-byline">Pathways</b>
        <span className="feed-pass-kind" style={{ color: 'var(--accent)' }}>
          Run
        </span>
        <span className="feed-pass-subject">
          {live
            ? gate?.mode === 'confirm'
              ? 'working the open ends with you'
              : 'working the open ends on its own'
            : died
              ? 'never reached an ending'
              : run.stop
                ? RUN_STOP_HEAD[run.stop]
                : ''}
        </span>
        <span className="feed-pass-meta">{meta}</span>
        {!live && passes.length > 0 && (
          <button
            className="feed-pass-toggle"
            aria-expanded={open}
            aria-label={open ? 'Fold the run’s moves away' : 'Read the run’s moves'}
            title={open ? 'Fold the moves away' : 'Read the moves'}
            onClick={() => setOpen((v) => !v)}
          >
            <Chevron open={open} />
          </button>
        )}
      </div>

      {died && <p className="gnote gnote--quiet">The tab closed while this run was going.</p>}

      {expanded && (
        <div className="feed-run-moves">
          {passes
            .filter((p) => p.state !== 'live')
            .map(({ op, move }) => {
              const label = move?.label ?? ''
              const result = move
                ? moveResult(move)
                : op.error
                  ? STATUS_WORD.didNotRun.toLowerCase()
                  : ''
              const tint = THOUGHT_TINT[op.kind]
              const target = move?.nodeId || null
              const row = (
                <>
                  <i aria-hidden style={{ background: tint }} />
                  <span className="feed-move-kind" style={{ color: tint }}>
                    {opLabel(op, { research })}
                  </span>
                  <span className="feed-move-label">{label}</span>
                  <span className="feed-move-result">{result}</span>
                </>
              )
              // A move about a node is a press back to it; the closing scan and
              // any move whose target is gone stay plain rows.
              return target ? (
                <button
                  key={op.id}
                  className="feed-move"
                  title={move ? `It went here because ${MOVE_REASON[move.why]}.` : undefined}
                  onClick={() => onPivot(target)}
                >
                  {row}
                </button>
              ) : (
                <span key={op.id} className="feed-move">
                  {row}
                </span>
              )
            })}
        </div>
      )}

      {/* Confirm mode's pause, in the stream: the proposed move waits here,
          where the person is reading, until they answer. */}
      {gate?.phase === 'waiting' && gate.now && <RunGate now={gate.now} />}

      {/* The pass in flight streams INSIDE the run's message — the growing
          part of the growing message. */}
      {livePass && (
        <PassMessage
          op={livePass.op}
          state="live"
          move={livePass.move}
          thought={thought}
          searching={searching}
          inRun={inRun}
          onPivot={onPivot}
        />
      )}

      {/* What it came to, once it came to something: four lines off the record,
          each a press to where it is read in full (`RunFound.tsx`). Under the
          moves when they are open, because an ending follows what led to it. */}
      {run.finishedAt !== undefined && (
        <RunFound run={run} onPivot={onPivot} onAccount={() => onRun(run.id)} />
      )}

      {run.finishedAt !== undefined && (
        <p className="feed-pass-why">
          <button className="linkish" onClick={() => onRun(run.id)}>
            The whole run’s account
          </button>
        </p>
      )}
    </div>
  )
}

/**
 * An ask-first run's pause, in the stream: the move the run proposes, priced,
 * and the three answers a person can give it.
 *
 * The proposal is the POLICY'S pick, read off `RunLive.now` — data the loop
 * published, never a model's text — so the pause spends nothing by existing,
 * and the chip's label, reason and price all come from the app's own tables
 * (the act chips' rule, applied to the run's own moves). The presses are the
 * loop's own steering functions — `declineHere` IS the run strip's "Not there" — so
 * the two surfaces showing one pause cannot mean different things by the same
 * words, and every one of them is a hand on a gate, not a spend of its own:
 * the model pass runs only after `approveMove` opens it.
 */
function RunGate({ now }: { now: NonNullable<RunLive['now']> }) {
  const session = useStore((s) => s.session)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const entry = useActiveEntry()
  // Through `auditorFor` — the resolution `runAudit` itself makes — never
  // against the selection's provider: the audit is priced by the BRANCH's
  // author's other house, and a figure resolved from the selection names the
  // wrong model, and can lean low, exactly when the two differ.
  const auditor = useMemo(
    () =>
      now.kind === 'audit'
        ? auditorFor({ modelId: entry.id, keys, customBase, customModel, session }, now.nodeId)
        : null,
    [now.kind, now.nodeId, entry.id, keys, customBase, customModel, session],
  )
  // Ready by the proposed move's own route, as the strip's Go is (`gateReady`),
  // never by the selection's key.
  const ready = gateReady(
    { modelId: entry.id, roleModels, keys, customBase, customModel },
    now.kind,
    auditor?.entry ?? null,
  )
  const tint = THOUGHT_TINT[now.kind] ?? 'var(--accent)'
  return (
    <div className="feed-acts">
      <span className="feed-acts-eyebrow">{STATUS_WORD.awaitingGo}</span>
      <div className="actchip">
        <span className="actchip-kind" style={{ color: tint }}>
          {OP_LABEL[now.kind]}
        </span>
        <span className="actchip-target" title={`It goes here because ${MOVE_REASON[now.why]}.`}>
          {now.label}
        </span>
        {ready ? (
          <VerbPrice
            verb={gatePriceVerb(now.kind)}
            auditor={now.kind === 'audit' ? (auditor?.entry ?? null) : undefined}
            inRun
          />
        ) : (
          <span className="vp-spent">{STATUS_WORD.needsKey}</span>
        )}
        <button className="btn btn--primary" onClick={approveMove}>
          Go
        </button>
      </div>
      <p className="feed-pass-why">
        <button
          className="linkish"
          disabled={!now.nodeId}
          onClick={declineHere}
          title="Closes this branch for the rest of the run and asks about another end."
        >
          Not there
        </button>
        {' · '}
        <button
          className="linkish"
          onClick={approveRest}
          title="A standing go: this move and every one after it, without being asked again."
        >
          On its own from here
        </button>
      </p>
    </div>
  )
}

/**
 * One pass, as a message that never un-says itself: the header names the verb
 * and its subject, the reasoning streams with a caret while the pass runs, and
 * when it lands NOTHING folds — the thinking stays, what landed renders as
 * readable lines under it, and a check's own result (reading, votes, verdict)
 * appears in the message the way an answer would. A chat keeps its words on
 * screen; the earlier fold-on-land made every landing a disappearance, which
 * read as a log even while the content was right.
 *
 * The chevron survives with its meaning inverted: it TUCKS a message the
 * reader is done with, by hand, back to its one-line row — never
 * automatically. The wall this component once folded against was the run's,
 * and the run message still handles that its own way (rows, one bubble).
 *
 * Two orders, by whether the reader saw it happen (D341). A pass this message
 * streamed keeps the order it was read in — trace, then what came of it under
 * the trace, where the reader's eyes already are — so a landing never pushes
 * the words being read down the screen. Every other finished pass — history
 * restored from disk, or one that landed while the reader was elsewhere —
 * leads with what came of it (the notes, the result, what landed), and its
 * trace sits under that behind one press, "Reasoning · N paragraphs": scrolling
 * back through a transcript used to mean reading every deliberation to learn
 * what it concluded. That press opens by itself for a pass this tab watched to
 * its end (`watchedLandings`), and for a pass that left nothing else to read,
 * where the trace IS the message. Nothing the reader watched is ever folded
 * away. A press on the line is that message's own and is not remembered, as
 * the tuck is not: one curious press must not open every restored trace in
 * every exploration from then on.
 *
 * A check's result renders only when the retained record belongs to this op.
 * Source checks are joined by operation id, including from the archive after a
 * claim card is removed; panel and audit records still use their timestamps.
 */
function PassMessage({
  op,
  state,
  move,
  underSeed = false,
  thought,
  searching,
  inRun,
  onPivot,
}: {
  op: Op
  state: PassState
  move?: RunMove
  /** Directly under the seed's bubble, which this pass answers (D359). */
  underSeed?: boolean
  thought: string
  searching: string | null
  inRun: boolean
  onPivot: (id: string) => void
}) {
  const session = useStore((s) => s.session)
  const archivedChecks = useMemo(() => orphanedVerificationEntries(session), [session])
  // Expanded is the RESTING state — tucking is the reader's own act, per
  // message, and it does not persist: a reload is a fresh reading of the
  // transcript, and the transcript is open.
  const [tucked, setTucked] = useState(false)
  const live = state === 'live'
  const expanded = live || !tucked
  // Whether THIS message streamed the pass — set on the first live render and
  // never unset, so the landing render still knows (React's derived-from-the-
  // previous-render pattern rather than an effect, which would draw one frame
  // in the other order first). A remount — another exploration and back, a
  // reload — starts unset, whatever happened before it.
  const [streamed, setStreamed] = useState(live)
  if (live && !streamed) setStreamed(true)
  // Watched to its end: recorded on landing, read by every later mount.
  useEffect(() => {
    if (streamed && !live && op.finishedAt !== undefined) watchedLandings.add(op.id)
  }, [streamed, live, op.id, op.finishedAt])
  // The reader's own press on this message's trace fold; null until they press
  // it. Per message and never persisted, like the tuck.
  const [traceChoice, setTraceChoice] = useState<boolean | null>(null)
  const traceId = useId()
  // A live pass while the run WAITS at its confirm gate is the person's own
  // hand-fired verb — the run has nothing in flight at a pause — so it gets
  // its Stop back, aborting the pass alone rather than the run around it.
  const runWaiting = useStore((s) => s.run?.phase === 'waiting')
  // The transport waiting to ask again (`waiting`): the only pass that can be
  // waiting is the live one. Its steps so far, the same way (`searchTrail`).
  const waiting = useStore((s) => (live ? s.waiting : null))
  const trail = useStore((s) => (live ? s.searchTrail : NO_STEPS))
  // How long one like it usually takes here, read once for this pass.
  const usual = useUsual(live ? op : null)

  // Untucking reveals DOWNWARD, and a row sitting at the feed's bottom edge
  // would unfold entirely below the reader — seating the row's head at the top
  // of the scroll puts what it just revealed on screen. Tucking moves nothing.
  // In the click handler rather than an effect, because the default state is
  // open: an effect keyed on the expanded flag would fight the mount scroll of
  // every message on load.
  const root = useRef<HTMLDivElement | null>(null)
  const toggleTuck = () => {
    if (tucked) requestAnimationFrame(() => root.current?.scrollIntoView({ block: 'start' }))
    setTucked(!tucked)
  }

  const tint = THOUGHT_TINT[op.kind]
  // Which pass of the exploration this was, the narrator's turns not counted —
  // the foot's own count (`ThreadFoot`), so "pass 3" here is the third of its N.
  const passNo = session.ops.filter((o) => !o.narration).findIndex((o) => o.id === op.id) + 1
  const target = op.targetIds.length === 1 ? session.nodes[op.targetIds[0]!] : undefined
  // Resolve before the header too: once a checked node is pruned, the retained
  // label is the only honest subject more specific than “a claim”.
  const { verification, panel, audit, priorArt, sweep } = passCheckResults(
    op,
    target,
    archivedChecks,
    session.sweeps,
  )
  const since = checkedAgainSince(target, verification)
  // An executable test's contract and its readings, derived from the receipts on
  // every read and stored nowhere (D283) — joined by the op's own id, so a Lab
  // analysis aimed at the same conjecture is never read as one.
  const executable = useMemo(() => executableOutcome(session, op), [session, op])
  const angles = op.portfolio?.parts.length ?? 0
  const subject =
    angles > 1
      ? `${angles} angles, one pass`
      : op.targetIds.length > 1
        ? `${op.targetIds.length} claims`
        : (target?.label ??
          verification?.labelAtCheck ??
          (op.targetIds.length ? 'a claim since pruned' : ''))
  // The opening pass, under the seed's bubble, would restate it word for word as
  // its subject — the seam a glued narration drops its own for. A root rewritten
  // since keeps its subject and the mark beside it: those are words the bubble
  // does not say.
  const restated =
    underSeed &&
    target !== undefined &&
    subject.trim() === session.seed.trim() &&
    !rewrittenSince(target, op.startedAt)

  const secs =
    op.finishedAt !== undefined
      ? Math.max(1, Math.round((op.finishedAt - op.startedAt) / 1000))
      : null
  // What the pass landed, the seed apart (D359). The seed is the reader's, in the
  // bubble at the head of the thread, and a pass never makes the words it was
  // asked about: only the worked example's authored opening lists the root among
  // its yield (`engine/demo.ts`), and printed there it said the bubble's sentence
  // again. The count leaves it out with its row.
  const yielded = op.produced.filter((id) => session.nodes[id]?.parentId !== null)
  const produced = yielded.map((id) => session.nodes[id]).filter((n) => n !== undefined)
  const meta = [
    yielded.length > 0 ? `${yielded.length} node${yielded.length === 1 ? '' : 's'}` : null,
    secs !== null ? `${secs}s` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  // What came of the pass, apart from its trace and its notes: the blocks a
  // restored message leads with.
  const hasResult =
    produced.length > 0 ||
    verification !== undefined ||
    panel !== undefined ||
    audit !== undefined ||
    priorArt !== undefined ||
    sweep !== undefined ||
    op.admission !== undefined ||
    executable !== null
  // Whether a tuck would hide anything — the chevron renders only where
  // pressing it changes something. A pass can legitimately have no trace (some
  // providers return none) and no yield; its row is then the whole message.
  const hasBody =
    live || Boolean(op.thinking) || Boolean(op.error) || Boolean(op.refused) || hasResult

  // The finished trace, as the paragraphs it is drawn in — what the fold's line
  // counts. The live trace reads `thought` instead, below.
  const paragraphs = live ? [] : (op.thinking ?? '').split('\n\n').filter((p) => p.trim())
  // Trace first only for the message that streamed it; see the comment above.
  const traceFirst = live || streamed
  const traceOpen = traceFirst || (traceChoice ?? (watchedLandings.has(op.id) || !hasResult))
  const toggleTrace = () => setTraceChoice(!traceOpen)

  // What happened, then what it left: nodes, a trace, or nothing. It read
  // "Stopped partway … the trace below is what arrived" over a rate limit that
  // turned the request away before a byte came back. Inside the trace while it
  // streams; a restored message says it above its result.
  const notes = (
    <>
      {op.error && (
        <p className="gnote gnote--warn">
          {passFailureNote(op, produced.length, Boolean(op.thinking))}
        </p>
      )}
      {op.refused && (
        <p className="gnote gnote--warn">
          {refusalSentence(
            declinedMessage('The model declined this line of reasoning.', op, op.model),
            produced.length,
          )}
        </p>
      )}
    </>
  )

  // The trace, and on a message that did not stream here its one line above it.
  // Drawn above the result by `traceFirst`, under it otherwise.
  const trace = (
    <>
      {/* The trace's one line, on those messages only: the message that
          streamed it keeps its trace where it was read, with no line pushed in
          above the words on landing. Hidden rather than unmounted below, so
          `aria-controls` never points at an id that is not in the document. */}
      {!traceFirst && paragraphs.length > 0 && (
        <button
          type="button"
          className="feed-trace-head"
          aria-expanded={traceOpen}
          aria-controls={traceId}
          title={traceOpen ? 'Fold the model’s reasoning away' : 'Read the model’s reasoning'}
          onClick={toggleTrace}
        >
          <Chevron open={traceOpen} />
          Reasoning · {paragraphs.length} paragraph{paragraphs.length === 1 ? '' : 's'}
        </button>
      )}

      {(traceFirst || paragraphs.length > 0) && (
        // Busy while it streams: the trace is being written, and a screen reader
        // that reads it now reads half a sentence; the announcer says when the
        // pass lands. The head, with its Stop, is outside it.
        <div className="feed-trace" id={traceId} hidden={!traceOpen} aria-busy={live || undefined}>
          {traceFirst && notes}
          {live
            ? thought
                .split('\n\n')
                .filter((p) => p.trim())
                .map((para, i, all) => (
                  <p key={i}>
                    {para}
                    {i === all.length - 1 && <i className="caret" />}
                  </p>
                ))
            : paragraphs.map((para, i) => <p key={i}>{para}</p>)}
          {/* Before any thought, a wait to ask again stands where the
              placeholder would: "reading the branch" through a wait the API
              asked for described a request nobody was reading. */}
          {live && !thought && (
            <p>
              {waiting ?? 'Reading the branch from the root down…'} <i className="caret" />
            </p>
          )}
          {/* Lines of their own rather than part of the trace. The trace is the
              model's reasoning; these are actions it took, and splicing one
              into the other would make the retained trace claim to contain
              something the model never wrote. The steps stay listed through a
              wait, which stands under them as what the pass is doing now. */}
          {live && searching && <SearchTrail trail={trail} quiet={Boolean(thought && waiting)} />}
          {live && thought && waiting && <p className="feed-search">{waiting}</p>}
          {!live && op.thinkingClipped && (
            <p className="gnote gnote--quiet">The tail of this trace was dropped.</p>
          )}
        </div>
      )}
    </>
  )

  return (
    <div className="feed-pass" data-live={live || undefined} ref={root}>
      {/* Focusable, never a tab stop: where focus lands when the press that
          started this pass went away under it (`focusLivePass`). */}
      <div className="feed-pass-head" data-pass-head tabIndex={-1}>
        {/* The byline (D352): the app's mark, its name, the kind of pass in its
            colour, and which pass of the exploration this was — counted as the
            foot counts them, the narrator's turns apart. The mark breathes while
            the pass is live, where the kind's dot did. */}
        <i className="feed-mark" data-live={live || undefined} aria-hidden />
        <b className="feed-byline">Pathways</b>
        <span className="feed-pass-kind" style={{ color: tint }}>
          {opLabel(op, session)}
        </span>
        {passNo > 0 && <span className="feed-pass-no">pass {passNo}</span>}
        {/* The claim this pass was about is a press back to it, as a turn's
            "about …" and every landed row already are; the subjects with nothing
            to open — several claims, a portfolio's angles, one since pruned —
            stay words (D335). */}
        {restated ? null : target && angles <= 1 ? (
          <button
            type="button"
            className="feed-pass-subject"
            title={target.claim}
            onClick={() => onPivot(target.id)}
          >
            {subject}
          </button>
        ) : (
          subject && <span className="feed-pass-subject">{subject}</span>
        )}
        {/* The pass read words the reader has since replaced: its trace and its
            finding are about those, not the claim on the card now. Beside the
            subject rather than inside it, so a long label's ellipsis cannot eat
            the one mark that says so. */}
        {target && rewrittenSince(target, op.startedAt) && (
          <span className="feed-since">{SINCE_REWRITTEN}</span>
        )}
        {/* While it runs, the clock stands where its seconds will once it lands. */}
        {(meta || live) && (
          <span className="feed-pass-meta">
            {meta}
            {meta && live && ' · '}
            {live && <PassClock since={op.startedAt} />}
          </span>
        )}
        {hasBody && !live && (
          <button
            className="feed-pass-toggle"
            aria-expanded={expanded}
            aria-label={expanded ? 'Tuck this message away' : 'Open this message back up'}
            title={expanded ? 'Tuck this message away' : 'Open this message back up'}
            onClick={toggleTuck}
          >
            <Chevron open={expanded} />
          </button>
        )}
        {/* Stop rides the live message only for a pass the run does not own: a
            lone pass, or one hand-fired into a confirm pause. During a run's
            own passes the run strip owns stopping — two stop affordances for one run
            would race each other (`Thoughtstream` keeps the same rule). */}
        {live && (!inRun || runWaiting) && (
          <button
            className="feed-pass-stop"
            aria-label="Stop this pass"
            title="Stop this pass — what has already arrived is kept, and it was billed either way"
            onClick={() => (inRun ? abortInFlight() : cancelCurrent())}
          >
            Stop
          </button>
        )}
      </div>

      {move && (
        <p className="feed-pass-why">
          Move {move.at} of the run — it went here because {MOVE_REASON[move.why]}.
        </p>
      )}

      {state === 'died' && (
        <p className="gnote gnote--quiet">This pass never finished — the tab closed under it.</p>
      )}

      {/* A message that did not stream here leads with what came of it — the
          notes, then the result and what landed — and its trace follows (D341). */}
      {expanded && !traceFirst && notes}
      {expanded && traceFirst && trace}

      {/* Under the trace, and outside its `aria-busy`: this is no part of the
          streaming text, and it is while the pass runs that it is worth hearing. */}
      {live && usual && (
        <p className="feed-usual">
          <UsualNote usual={usual} op={op} />
        </p>
      )}

      {/* A check's own answer, in the message — a source check that showed only
          "3s" was a reply with the words missing. Guarded by `within`, so an
          old pass never wears a newer check's finding. */}
      {expanded && verification && (
        <div className="feed-result">
          <p>
            <b className="feed-reading">{READING_LABEL[verification.reading]}</b>
            {verification.summary ? <> — {verification.summary}</> : null}
          </p>
          <p className="feed-result-meta">
            {/* The sources are one press away (D335): it opens the claim's card
                on its check record, where they are listed with their quotes.
                Only while the claim is still on the graph with a record to go
                to; a pruned claim's message keeps the plain count. */}
            {target?.verification ? (
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  onPivot(target.id)
                  scrollToCheckRecord(target.id)
                }}
              >
                from {verification.sources.length} source
                {verification.sources.length === 1 ? '' : 's'}
              </button>
            ) : (
              <>
                from {verification.sources.length} source
                {verification.sources.length === 1 ? '' : 's'}
              </>
            )}
            {/* How they sided, in the card's strip: the count the reply rests on,
                where "from 5 sources" had said only how many. */}
            {verification.sources.length > 0 && (
              <>
                : <StanceStrip sources={verification.sources} />
              </>
            )}
            {verification.partial ? ' · a partial look' : ''}
            {/* A check a later one replaced says what the record reads now: a
                refutation since overturned read, here, as the claim's standing. */}
            {since ? ` · checked again since: ${READING_LABEL[since.reading].toLowerCase()}` : ''}
          </p>
        </div>
      )}
      {expanded && panel && (
        <div className="feed-result">
          {panel.votes.map((v) => (
            <p key={v.lens}>
              <b className="feed-reading">
                {LENS_LABEL[v.lens]}: {PANEL_VERDICT_LABEL[v.verdict]}
              </b>{' '}
              — {v.because}
            </p>
          ))}
          {panel.lenses.length > panel.votes.length && (
            <p className="feed-result-meta">
              {panel.lenses.length} lenses asked, {panel.votes.length} returned.
            </p>
          )}
        </div>
      )}
      {expanded && audit && (
        <div className="feed-result">
          <p>
            <b className="feed-reading">{auditFindingLabel(audit)}</b> — {audit.note}
          </p>
        </div>
      )}
      {expanded && sweep && (
        <div className="feed-result">
          <p>{sweepSentence(sweep)}</p>
          {sweep.items.map((i) => (
            <p key={i.id} className="feed-result-meta">
              {SWEEP_KIND_LABEL[i.kind]} · {hostOf(i.url)} — {i.statement}
            </p>
          ))}
        </div>
      )}
      {expanded && priorArt && (
        <div className="feed-result">
          <p>
            <b className="feed-reading">
              {priorArtLabel(
                priorArt,
                target?.conjecture ? laterStatements(target.conjecture, priorArt) : [],
              )}
            </b>{' '}
            —{' '}
            {priorArtNote(
              priorArt,
              undefined,
              target?.conjecture ? laterStatements(target.conjecture, priorArt) : [],
            )}
          </p>
          <p className="feed-result-meta">
            {priorArt.queries.length} quer{priorArt.queries.length === 1 ? 'y' : 'ies'} ·{' '}
            {priorArt.seen} page{priorArt.seen === 1 ? '' : 's'} returned ·{' '}
            {priorArt.backstop.close} close by term overlap
          </p>
        </div>
      )}
      {/* A conjecture pass's admission tally, kept in its message: it was said
          only in the toast, so an empty pass's message went on saying nothing
          once the toast had gone — and the count is what D278 says must be said.
          An empty pass says so in D281's words, as its run row does. */}
      {expanded && op.admission && (
        <div className="feed-result">
          {op.produced.length === 0 && !op.error && !op.refused && (
            <p>
              <b className="feed-reading">{STATUS_WORD.noConjectureLanded}</b>
            </p>
          )}
          <p className="feed-result-meta">{admissionSentence(op.admission)}</p>
        </div>
      )}
      {expanded && executable && <ExecutableResult outcome={executable} />}

      {/* What landed, as the message's own words: one line per node — the
          label a press back into the graph, the claim the sentence it stands
          for. Lines rather than chips, however many, because a chip wall says
          "twenty-eight things happened" while a chat message says what they
          are; the demo's big opening is accepted as a long first message. */}
      {expanded && produced.length > 0 && (
        <div className="feed-landed">
          {produced.map((n) => (
            <p key={n.id} className="feed-landed-row">
              <button
                className="feed-landed-name"
                style={{ '--fork-tint': `var(--sem-${kindChannelOf(n)})` } as CSSProperties}
                onClick={() => onPivot(n.id)}
              >
                <i aria-hidden />
                {n.label}
              </button>
              {/* The ending in the shared vocabulary — an objection that stood,
                  a path that failed and a question reasoning can't settle are
                  three different findings and used to share one word. */}
              {n.terminal && <span className="feed-landed-dead">{endingLabelOf(n)}</span>}
              {/* The label and claim below are the reader's words now, not what
                  this pass landed. */}
              {rewrittenSince(n, op.finishedAt ?? op.startedAt) && (
                <span className="feed-since">{SINCE_REWRITTEN}</span>
              )}
              {/* A claim that IS its label — printing both would say the same
                  sentence twice. */}
              {n.claim.trim() !== n.label.trim() && (
                <span className="feed-landed-claim">{n.claim}</span>
              )}
            </p>
          ))}
        </div>
      )}

      {/* Under what came of it, on a message that did not stream here (D341). */}
      {expanded && !traceFirst && trace}
    </div>
  )
}

/**
 * What an executable-test pass left, in its own message (D283, D289): no
 * contract, or the one it wrote and what its executions read. Derived from the
 * receipts on every render (`executableOutcome`) and stored nowhere, so a receipt
 * that lands in Research Lab later changes this line and nothing else could
 * disagree with it. A reading is printed in the conjecture card's own sentence —
 * who wrote the evaluator, and what qualifies it — and beside the results a
 * person records, never as one. The contract's title is the model's words, as the
 * trace above it is.
 */
function ExecutableResult({ outcome }: { outcome: ExecutableRecord | 'none' }) {
  if (outcome === 'none') {
    return (
      <div className="feed-result">
        <p>
          <b className="feed-reading">{NO_TEST_ADDED}</b>
        </p>
      </div>
    )
  }
  const latest = outcome.results[0]
  // Receipts on record whose reading needs the conjecture as the contract named
  // it, and this graph no longer has that to read them against.
  const unread = outcome.executions - outcome.results.length
  const count = (n: number) => `${n} execution${n === 1 ? '' : 's'}`
  if (!outcome.executions) {
    return (
      <div className="feed-result">
        <p>
          <b className="feed-reading">{EXECUTABLE_NOT_RUN}</b> — {outcome.spec.title}
        </p>
        <p className="feed-result-meta">
          It measures {outcome.spec.metric.name} in {outcome.spec.metric.unit}:{' '}
          {rangesSentence(outcome)}, both declared before anything ran. Running it is your press in
          Research Lab → Tests. A model wrote its programs, its data and its evaluator, and nobody
          has reviewed them.
        </p>
      </div>
    )
  }
  return (
    <div className="feed-result">
      {latest ? (
        <p>{executedSentence(latest)}</p>
      ) : (
        <p>
          <b className="feed-reading">{outcome.spec.title}</b>
        </p>
      )}
      <p className="feed-result-meta">
        {latest && outcome.results.length > 1
          ? `The latest of ${count(outcome.results.length)}. `
          : ''}
        {unread
          ? `${count(unread)} on record that this graph cannot read against the insight; their receipts are in Research Lab → Tests. `
          : ''}
        {latest ? EXECUTED_NOT_A_RESULT : ''}
      </p>
    </div>
  )
}

/**
 * One question and its answer, in the chat's own grammar: the reader's words
 * ride a right-aligned bubble — the one visual convention every chat surface
 * has taught, and the marker that makes "who said this" legible at a glance —
 * while the model's answer stays the left lane's plain prose, roomy the way an
 * answer wants to be. The subject stamp above the bubble is the pin rule kept
 * per turn: it names what the question was asked about, in the words of that
 * moment, and is a press back to the node while it exists. The stamp for the
 * CURRENT selection is skipped — under it, "about this" is already true, and a
 * chip repeating the selection on every turn is noise.
 */
function TurnMessage({
  turn,
  glue,
  actsLive,
  selectedId,
  onPivot,
}: {
  turn: FeedTurn
  glue: boolean
  actsLive: boolean
  selectedId: string | null
  onPivot: (id: string) => void
}) {
  const session = useStore((s) => s.session)
  const about = turn.about
  const node = about?.kind === 'node' && about.id ? session.nodes[about.id] : undefined
  // A glued narration sits directly under the pass whose header already names
  // its subject — restating it would be the seam the glue exists to hide.
  const showSub =
    !glue && about !== undefined && !(about.kind === 'node' && about.id === selectedId)
  // A Stop that ended this narration took itself away — the turn's own, or the
  // phone box's — and left focus on the page body. The line saying it stopped is
  // what replaced it (D217's rule), so it takes focus: once, as the turn stops,
  // only while focus really is lost, and only after a Stop somebody pressed.
  const note = useRef<HTMLParagraphElement | null>(null)
  useEffect(() => {
    if (turn.live || !turn.voice || !turn.error || Date.now() - voiceStoppedAt > 2000) return
    const here = document.activeElement
    if (here && here !== document.body) return
    note.current?.focus({ preventScroll: true })
  }, [turn.live, turn.voice, turn.error])
  // The turn's own words — the voice's name or the question, the answer, and
  // what the answer is doing — in a card of their own when the voice speaks
  // (D356): the design's coach card holds its words, and the moves it proposes
  // stand under it, outside the card, as the column's own block.
  const body = (
    <>
      {/* A voice turn is the model speaking unprompted — no question, so no
        bubble. What it gets instead is a name that says what its words are.
        Glued under a source check's header, the voice's commentary otherwise
        read as the check's own finding; an earlier build's eyebrow, "Research
        analysis", read as something weightier still. */}
      {turn.voice ? (
        // A card's head (D352): the voice's name, and what its words are not —
        // the one label, its two halves, so neither can drift from the receipt.
        <span className="feed-voice-mark">
          <b>{VOICE_NAME}</b>
          <span className="feed-voice-pill">{VOICE_UNCHECKED}</span>
        </span>
      ) : (
        <p className="chat-you">{turn.question}</p>
      )}
      <Prose
        text={turn.answer}
        live={turn.live}
        refs={{
          nodes: session.nodes,
          onPivot,
          onPoint: pointAtClaim,
          onRelease: releaseClaim,
        }}
      />
      {turn.live && turn.waiting && <p className="gnote">{turn.waiting}</p>}
      {/* The voice's Stop reaches the narration's own request (`stopNarration`)
        and nothing else — not the pass it follows, not an Ask. It sits where
        the words are read, on a desk and on a phone alike. */}
      {turn.live && turn.voice && (
        <button
          className="linkish feed-turn-stop"
          aria-label="Stop the narration"
          title="Ends the narration. The pass above is unaffected, and nothing more is spent."
          onClick={() => {
            voiceStoppedAt = Date.now()
            stopNarration(turn.id)
          }}
        >
          Stop
        </button>
      )}
      {turn.live && !turn.voice && (
        <button className="linkish feed-turn-stop" onClick={stopAsking}>
          Stop
        </button>
      )}
      {turn.error && (
        <p className="gnote gnote--warn" ref={note} tabIndex={turn.voice ? -1 : undefined}>
          {turn.error}
        </p>
      )}
    </>
  )
  return (
    <div
      className="ask-turn feed-turn"
      data-glued={glue || undefined}
      data-voice={turn.voice || undefined}
    >
      {showSub &&
        (node ? (
          <button className="feed-turn-sub" onClick={() => onPivot(node.id)} title={node.claim}>
            about {about.label}
            {/* Asked about words the reader has since replaced. */}
            {rewrittenSince(node, turnAt(turn)) && ` ${SINCE_REWRITTEN}`}
          </button>
        ) : (
          <span className="feed-turn-sub" data-gone>
            about {about.kind === 'graph' ? 'this exploration' : `${about.label} — since pruned`}
          </span>
        ))}
      {turn.voice ? <div className="feed-voice-card">{body}</div> : body}
      {!turn.live && !turn.error && turn.acts && turn.acts.length > 0 && (
        <ActRow acts={turn.acts} since={turnAt(turn)} live={actsLive} />
      )}
    </div>
  )
}

/**
 * The chips a reply proposed: language become controls, with the press still a
 * press. Every row is RE-VALIDATED against the app's own tables at render —
 * the kind against the act vocabulary, the node against the graph as it stands
 * now, the label and the price from the session and the one arithmetic, never
 * from the model's text — so a stored proposal that has gone stale simply
 * renders fewer chips. The model's own `why` rides the tooltip: its words may
 * gloss a button, never write one. Presses go through `takeAct`, the one
 * dispatcher, and the refusal a greyed chip prints below is the sentence the
 * press would raise (`promotePress`'s pair of properties, one door over).
 *
 * Only the thread's newest proposal row is buttons (`live`). An older one folds
 * to a line that still says what was proposed — the record of the suggestion
 * stays, the offer does not. And a chip whose verb has already run on its target
 * since the proposal reads "Done" in place of its price and loses its button,
 * read off the stored ops by `proposalState` and never off the model's words: a
 * second press there would buy the same pass twice.
 *
 * Under the lock a move, check or panel chip is still an offer: its press joins
 * the Up next line at the feed's tail, as the same press from a key or the card
 * does, and the chip then reads "Up next" and loses its button until the pass
 * runs (and reads Running, then Done) or the line lets it go (✕, Stop, a chip
 * that asks again) and it is an offer again.
 */
function ActRow({
  acts,
  since,
  live,
}: {
  acts: NonNullable<FeedTurn['acts']>
  /** When the proposal was made — the turn's own time, `turnAt`. */
  since: number
  live: boolean
}) {
  const session = useStore((s) => s.session)
  const busy = useStore((s) => s.busy)
  // What the Up next line reads to take a press or refuse it — the chip's paid
  // press is that press (D162), so its refusal is the line's.
  const interrupt = useStore((s) => s.interrupt)
  const run = useStore((s) => s.run)
  const upNext = useStore((s) => s.upNext)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  // Each chip's figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const route = { modelId, roleModels }
  // A cross-examination chip reads the house its press's pass resolves, per
  // claim (`actAuditor`), never one resolved against the selection's house.
  const auditorView = { modelId, keys, customBase, customModel, session }

  // This exploration's line only: another's presses are not this thread's.
  const waiting = upNextHere({ session, upNext })
  const rows = acts.flatMap((a) => {
    const kind = asActKind(a.kind)
    if (!kind) return []
    const node = session.nodes[a.node]
    if (!node) return []
    return [{ kind, node, why: a.why, state: proposalState(session, a, since, busy, waiting) }]
  })

  // Focus, when a chip's own press takes its button away. The button gives way
  // to its state word — Running, Up next, Done — as soon as the press is taken,
  // and the key that pressed it was left on the page body. So the word takes
  // focus (D217's rule: focus lands on what replaced the press), once, only
  // while focus really is lost, and only a moment after the press.
  const stateWords = useRef(new Map<string, HTMLElement>())
  const owed = useRef<{ key: string; at: number } | null>(null)
  useEffect(() => {
    const o = owed.current
    if (!o) return
    const row = rows.find(({ kind, node }) => `${kind}:${node.id}` === o.key)
    if (row && row.state === 'open') {
      if (Date.now() - o.at > 10_000) owed.current = null
      return
    }
    owed.current = null
    const here = document.activeElement
    if (here && here !== document.body) return
    stateWords.current.get(o.key)?.focus({ preventScroll: true })
  })

  // The claim a chip is pointing at, held until the chip lets go. A row that folds
  // or unmounts under the pointer fires no leave, and would leave its ring on.
  const held = useRef<string | null>(null)
  const point = (id: string) => {
    held.current = id
    pointAtClaim(id)
  }
  const release = (id: string) => {
    if (held.current === id) held.current = null
    releaseClaim(id)
  }
  useEffect(
    () => () => {
      if (held.current) releaseClaim(held.current)
      held.current = null
    },
    [live],
  )

  if (rows.length === 0) return null

  // An older proposal, folded: what was proposed, in words, and no press. The
  // state still reads through, so a suggestion somebody took says so here too.
  if (!live) {
    return (
      <p className="feed-acts-past">
        <span className="feed-acts-eyebrow">Proposed</span>{' '}
        {rows.map(({ kind, node, state }, i) => (
          <span key={`${kind}:${node.id}`}>
            {i > 0 && ' · '}
            {ACT_NAME[kind]} on “{node.label}”
            {state !== 'open' && ` — ${STATUS_WORD[state].toLowerCase()}`}
          </span>
        ))}
      </p>
    )
  }

  const view = {
    busy,
    interrupt,
    run,
    session,
    upNext,
    modelId,
    roleModels,
    keys,
    customBase,
    customModel,
  }
  // Only the chips that are still offers can be refused: a done one — or one
  // already up next — has no press left to refuse, and printing a sentence under
  // it would explain a button that is not there.
  const refusals = rows
    .filter(({ state }) => state === 'open')
    .map(({ kind, node }) => actRefusal(view, kind, node.id))
    .filter((w) => w !== null)

  return (
    <div className="feed-acts">
      {/* What these are, and the promise that comes with them (D352). */}
      <p className="feed-acts-head">
        <span className="feed-acts-eyebrow">Proposed next moves</span>
        <span className="feed-acts-note">Nothing runs until you press it</span>
      </p>
      {rows.map(({ kind, node, why, state }) => {
        const verb = actPriceVerb(kind)
        const refusal = actRefusal(view, kind, node.id)
        const tint =
          kind === 'record' || kind === 'edit' ? 'var(--ink-insight)' : THOUGHT_TINT[kind]
        const auditor = actAuditor(auditorView, kind, node.id)
        const priceAuditor = kind === 'audit' ? auditor : undefined
        // The price cell tells the keyless truth: a reserve-backed verb unfurls
        // an authored branch and spends nothing, and every other paid press
        // leads to the key prompt — a dollar figure on either would price a call
        // that will not happen (NodeCard's own demo honesty, on the chip). A
        // proposal already acted on prints its state instead: there is no press
        // left to price. ONE reading, for the cell and for the button's name, so
        // what is seen and what is read aloud cannot say two things of one press.
        const word =
          state !== 'open'
            ? STATUS_WORD[state]
            : !verb
              ? STATUS_WORD.free
              : // The route this chip's pass runs on, not the selection's key.
                !actReady({ ...route, keys, customBase, customModel }, kind, auditor)
                ? kind in OP_HINT && hasDemoReserve(node.id, kind as RunVerb)
                  ? STATUS_WORD.demo
                  : STATUS_WORD.needsKey
                : null
        // The figure `VerbPrice` adds to the running estimate, through the same
        // arithmetic — the name reads the one number this press buys.
        const est = word === null && verb ? projectVerbUsd(route, verb, priceAuditor) : null
        const tail = word !== null ? word.toLowerCase() : est !== null ? pressEstimate(est) : null
        return (
          <div
            key={`${kind}:${node.id}`}
            className="actchip"
            data-state={state}
            // Where the chip points: its claim ringed on the canvas, and pulsed
            // once as the chip is reached, by the pointer or by focus. Released
            // only when neither still holds it (`ui/proposalMark.ts`).
            onPointerEnter={() => point(node.id)}
            onPointerLeave={(e) => {
              if (!e.currentTarget.contains(document.activeElement)) release(node.id)
            }}
            onFocus={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) point(node.id)
            }}
            onBlur={(e) => {
              const chip = e.currentTarget
              if (!chip.contains(e.relatedTarget as Node | null) && !chip.matches(':hover'))
                release(node.id)
            }}
          >
            <span className="actchip-kind" style={{ color: tint }} title={why}>
              {ACT_NAME[kind]}
            </span>
            <span className="actchip-target" title={node.claim}>
              {node.label}
            </span>
            {word !== null ? (
              <span
                className={state !== 'open' ? 'vp-spent actchip-state' : 'vp-spent'}
                // Where focus goes when this chip's press replaces its button.
                ref={(el) => {
                  const key = `${kind}:${node.id}`
                  if (el && state !== 'open') stateWords.current.set(key, el)
                  else stateWords.current.delete(key)
                }}
                tabIndex={state !== 'open' ? -1 : undefined}
              >
                {word}
              </span>
            ) : (
              verb && <VerbPrice verb={verb} auditor={priceAuditor} />
            )}
            {state === 'open' && (
              <button
                className="btn btn--primary"
                // Refused, but still in the tab order: under the lock a chip's
                // own press greyed the rest of the row, and a `disabled` chip
                // under the key dropped focus. The press says why (`takeAct`
                // raises the sentence printed below).
                aria-disabled={refusal !== null || undefined}
                // The whole press in words — the verb, the claim, and the price
                // or the reason there is none — where a screen reader used to
                // land on "Run", a dozen times down one thread.
                aria-label={actPressName(kind, node.label, tail)}
                onClick={() => {
                  // A paid press only: a form's chip reads Done when the reader
                  // records, from inside the form, long after this press.
                  owed.current = verb ? { key: `${kind}:${node.id}`, at: Date.now() } : null
                  if (!takeAct(kind, node.id)) owed.current = null
                }}
              >
                {actPressWord(kind)}
              </button>
            )}
          </div>
        )
      })}
      {refusalsToPrint(refusals).map((why) => (
        <p key={why} className="gnote gnote--warn">
          {why}
        </p>
      ))}
    </div>
  )
}

/**
 * The doors from the latest answer back into the record — `AskThread`'s row,
 * rendered under the feed's last turn under D42's own rule (see the placement
 * condition in `Feed`). The answer is a priced pass's INPUT, never the output
 * that lands; `core/promote.ts` owns which presses a node gets and
 * `promotePress.ts` is the one dispatcher.
 */
function FeedPromotes({
  node,
  question,
  answer,
}: {
  node: { id: string; parentId: string | null }
  question: string
  answer: string
}) {
  const session = useStore((s) => s.session)
  const storeBusy = useStore((s) => s.busy)
  const modelId = useStore((s) => s.modelId)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const roleModels = useStore((s) => s.roleModels)

  const promotes = promotesFor({ kind: 'node', isRoot: node.parentId === null })
  const routing = { session, modelId, roleModels, keys, customBase, customModel, busy: storeBusy }
  const refusals = new Map<PromoteKind, string>()
  for (const p of promotes) {
    const why = promoteRefusal(routing, p.kind, node.id)
    if (why !== null) refusals.set(p.kind, why)
  }

  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      <div className="tierline">
        <span className="eyebrow">Take it back to the graph</span>
        <SpentMeta />
      </div>
      <div className="actions ask-promotes">
        {promotes.map((p) => {
          const verb = PROMOTE_PRICE_VERB[p.kind]
          return (
            <button
              key={p.kind}
              className="action action--wide"
              // Refused, but still in the tab order (`aria-disabled`); the press
              // says why, in the sentence printed below (`takePromote`).
              aria-disabled={refusals.has(p.kind) || undefined}
              onClick={(e) => {
                const pressed = e.currentTarget
                takePromote(p.kind, node.id, { question, answer })
                // A paid press ends this row: its pass is now the feed's last
                // thing, and the row stands only under a finished answer (D42).
                // Focus goes to the pass that replaced it, not the page body.
                if (PROMOTE_PAID[p.kind]) focusLivePass(pressed)
              }}
            >
              <b>
                {p.label}
                {verb && <VerbPrice verb={verb} />}
                {!PROMOTE_PAID[p.kind] && <span className="vp-spent">{STATUS_WORD.free}</span>}
              </b>
              <span>{p.does}</span>
            </button>
          )
        })}
      </div>
      {[...new Set(refusals.values())].map((why) => (
        <p key={why} className="gnote gnote--warn">
          {why}
        </p>
      ))}
    </div>
  )
}
